import {
  clearExpiringTabToken,
  getExpiringTabToken,
  setExpiringTabToken,
} from '@/lib/expiring-tab-token';

/**
 * Holds a Bearer token for the ADMIN'S OWN session, scoped to the one tab
 * that entered the admin terminal.
 *
 * (Comment-only touch — no behavior change — to open a scratch PR for a
 * per-PR preview environment, for manually re-verifying the admin terminal
 * after a production rollback. Second touch: force a rebuild AFTER the PR
 * was opened, to test whether Vercel's VERCEL_GIT_PULL_REQUEST_ID only
 * populates on a build that happens once the PR exists.)
 *
 * `impersonation-token.ts` solves half of multi-tab admin: a tab that
 * IMPERSONATES gets its own identity, independent of the shared session
 * cookie. But that leaves a bystander tab exposed — one that is simply
 * sitting on the admin panel, never impersonating anything, still
 * authenticates purely off the shared cookie (production web is
 * cookie-only). The moment ANOTHER tab impersonates, Better Auth's Set-Cookie
 * rewrites that SAME cookie for the whole origin, and the bystander tab's
 * very next request — an org list refresh, a page navigation — now
 * authenticates as whatever the other tab just became. Verified directly
 * against the real auth stack: a plain cookie-only client went from 200 to
 * 403 the instant an unrelated tab impersonated, having done nothing itself.
 *
 * The fix is the same shape as impersonation's: give every admin-terminal tab
 * its OWN Bearer too, sourced the moment it enters the terminal (either by
 * clearing the 2FA gate itself, or by finding the gate already cleared via
 * the shared cookie — `two-factor-gate.tsx`'s `GET admin-terminal/session`
 * probe covers both). `apiClient`'s auth provider checks this AFTER the
 * impersonation override and BEFORE the cookie fallback, so an impersonating
 * tab is unaffected and a bystander tab stops depending on a cookie another
 * tab can rewrite out from under it.
 *
 * WHY THIS TOKEN IS A BIGGER DEAL THAN THE IMPERSONATION ONE. It is not a
 * freshly minted, short-lived session — it IS the admin's real 7-day session
 * token, the same value the HttpOnly cookie already holds, just also placed
 * here so this tab can send it as a Bearer. This is the same category of
 * exposure ENG-577 H4 rejected for a general 7-day web bearer, deliberately
 * re-accepted here for a much narrower population (platform admins only, who
 * have already cleared 2FA to reach this code at all) rather than everyone —
 * and bounded to `TAB_TOKEN_MAX_AGE_MS` (see `expiring-tab-token.ts`)
 * client-side, on top of sessionStorage's own tab-lifetime bound.
 */
const KEY = 'borradh.admin.tabBearerToken';

export const getAdminTabToken = (): string | undefined =>
  getExpiringTabToken(KEY);

export const setAdminTabToken = (token: string): void =>
  setExpiringTabToken(KEY, token);

export const clearAdminTabToken = (): void => clearExpiringTabToken(KEY);
